// timeline.ts
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export class Timeline {
    private timelineItems: NodeListOf<HTMLElement>;
    private timelineBullets: NodeListOf<HTMLElement>;
    private timelineHeadings: NodeListOf<HTMLElement>;
    private timelineLine: HTMLElement | null;
    private timelineList: HTMLOListElement | null;

    constructor(element: HTMLElement) {
        gsap.registerPlugin(ScrollTrigger);

        this.timelineLine = element;
        this.timelineItems = this.timelineLine.querySelectorAll(
            ".timeline-item .timeline-content"
        );
        this.timelineBullets = this.timelineLine.querySelectorAll(
            ".timeline-item .timeline-bullet"
        );
        this.timelineHeadings = this.timelineLine.querySelectorAll(
            ".timeline-item .timeline-heading"
        );
        this.timelineList = this.timelineLine.querySelector("ol");

        this.initAnimations();
        this.initProgressBar();
    }

    private initAnimations(): void {
        if (this.timelineList) {
            gsap.fromTo(
                this.timelineList,
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    duration: 2,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: this.timelineList,
                        start: "top 80%", // when top of list hits 80% of viewport
                        toggleActions: "play none none reverse",
                    },
                }
            );
        }

        this.timelineItems.forEach((item, index) => {
            const isLargeScreen = window.innerWidth >= 800; // 50rem = 800px
            var isEven = index % 2 === 0;

            if (!isLargeScreen) {
                isEven = true;
            }

            gsap.fromTo(
                item,
                {
                    opacity: 0,
                    x: isEven ? 100 : -100,
                },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: item,
                        start: "top center",
                        toggleActions: "play none none reverse",
                    },
                }
            );
        });

        this.timelineBullets.forEach((item, index) => {
            if (item.classList.contains("date-has-passed")) {
                gsap.fromTo(
                    item,
                    {
                        backgroundColor: "#171717",
                    },
                    {
                        backgroundColor: "#0087d7",
                        duration: 0.5,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: item,
                            start: "top center",
                            toggleActions: "play none none reverse",
                        },
                    }
                );
            }


            gsap.fromTo(
                item,
                {
                    scale: 1,
                },
                {
                    scale: 1.5, // scale up to 2
                    duration: 0.2, // speed of scale up
                    yoyo: true, // scale back to 1
                    repeat: 1, // do the yoyo once
                    ease: "power1.inOut", // smooth easing
                    scrollTrigger: {
                        trigger: item,
                        start: "top center", // when animation starts
                        toggleActions: "play none none reverse",
                    },
                }
            );
        });

        this.timelineHeadings.forEach((item, index) => {
            if (item.classList.contains("date-has-passed")) {
                gsap.fromTo(
                    item,
                    {
                        color: "#171717",
                    },
                    {
                        color: "#0087d7",
                        duration: 0.5,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: item,
                            start: "top center",
                            toggleActions: "play none none reverse",
                        },
                    }
                );
            }
        });
    }

    // New: Animate vertical progress bar (::before)
    private initProgressBar(): void {
        if (!this.timelineList) return;

        let stopProgressing = false; // flag to stop updating when past first unpassed item

        const scrollTrigger = ScrollTrigger.create({
            trigger: this.timelineList,
            start: "top center",
            end: "bottom center",
            scrub: true,
            onUpdate: (self) => {
                const items = Array.from(
                    this.timelineList!.querySelectorAll<HTMLElement>(".timeline-item")
                );

                const firstUnpassed = items.find(
                    (el) => !el.classList.contains("date-has-passed")
                );

                if (!firstUnpassed) {
                    stopProgressing = false;
                } else {
                    const elementTop = firstUnpassed.getBoundingClientRect().top + window.scrollY;
                    const stopScrollY = elementTop - window.innerHeight / 2;
                    stopProgressing = window.scrollY >= stopScrollY + 10;
                }

                if (!stopProgressing) {
                    const progressPercent = self.progress * 100;
                    this.timelineList!.style.setProperty(
                        "--progress-height",
                        `${progressPercent}%`
                    );
                }
            },
        });

        // Recalculate positions when ScrollTrigger refreshes (e.g., on page load or resize)
        ScrollTrigger.addEventListener("refresh", () => scrollTrigger.refresh());

        // Force initial update after refresh so the progress is correct if page is already scrolled
        ScrollTrigger.refresh();
    }

}


// Auto-init
function initTimeline() {
    const timelines = Array.from(
        document.querySelectorAll<HTMLElement>(".blockTimeline")
    );
    if (timelines.length === 0) return;

    const instances: Timeline[] = [];
    for (const el of timelines) {
        const instance = new Timeline(el);

        instances.push(instance);
    }
    (window as any)._timelineInstances = instances;
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initTimeline);
} else {
    initTimeline();
}
